import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Ic } from './Icon';
import { gsap, nf, prefersReducedMotion } from './motion';
import { SectionHead, delay } from './Section';
import type { LandingContent } from './content';

const MIN = 3;
const MAX = 150;
/* Per truck per month: ~3,500 L diesel, 15–20% saved ≈ 500 L × ~14 EGP + theft prevention ≈ 3,200 EGP */
const FUEL = 3200;
const MAINT = 1400;
const HOURS = 6;

const calc = (n: number) => ({ fuel: n * FUEL, maint: n * MAINT, hours: n * HOURS, total: n * (FUEL + MAINT) });

export function RoiCalculator({ c, onOpenDemo }: { c: LandingContent; onOpenDemo: () => void }) {
  const [n, setN] = useState(20);
  const [shown, setShown] = useState(() => calc(20));
  const live = useRef(calc(20));

  useEffect(() => {
    const target = calc(n);
    if (prefersReducedMotion()) {
      live.current = target;
      setShown(target);
      return;
    }
    const tw = gsap.to(live.current, { ...target, duration: 0.5, ease: 'power2.out', overwrite: true, onUpdate: () => setShown({ ...live.current }) });
    return () => {
      tw.kill();
    };
  }, [n]);

  const pct = `${((n - MIN) / (MAX - MIN)) * 100}%`;
  const R = c.roi;

  return (
    <section className="lp-section" id="roi" style={{ paddingTop: 0 }}>
      <div className="lp-container">
        <SectionHead eyebrow={R.eyebrow} title={R.title} sub={R.sub} />
        <div className="roi">
          <div className="lp-card roi-ctl" data-reveal>
            <span className="tag" style={{ fontSize: 13, fontWeight: 600, color: 'var(--muted)' }}>
              {R.sliderLabel}
            </span>
            <div className="big">
              <span>{n}</span>
              <small>{R.unit}</small>
            </div>
            <label className="sr-only" htmlFor="roiRange">
              {R.sliderLabel}
            </label>
            <input
              type="range"
              id="roiRange"
              min={MIN}
              max={MAX}
              step={1}
              value={n}
              onChange={(e) => setN(Number(e.target.value))}
              style={{ '--pct': pct } as CSSProperties}
            />
            <div className="range-labels">
              <span>3</span>
              <span>50</span>
              <span>100</span>
              <span>150+</span>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: 13.5, lineHeight: 1.7, marginTop: 8 }}>{R.note}</p>
          </div>

          <div className="lp-card roi-res" data-reveal style={delay(120)}>
            <b style={{ fontSize: 15 }}>{R.resultsTitle}</b>
            <ResultRow title={R.fuel} desc={R.fuelDesc} value={nf.format(Math.round(shown.fuel))} unit={R.currency} />
            <ResultRow title={R.maint} desc={R.maintDesc} value={nf.format(Math.round(shown.maint))} unit={R.currency} />
            <ResultRow title={R.hours} desc={R.hoursDesc} value={nf.format(Math.round(shown.hours))} unit={R.hoursUnit} />
            <div className="roi-total">
              <span>{R.total}</span>
              <b>
                {nf.format(Math.round(shown.total))} {R.egp}
              </b>
            </div>
            <button type="button" className="lp-btn lp-btn-primary" onClick={onOpenDemo} style={{ marginTop: 6 }}>
              <span>{R.cta}</span>
              <Ic name="arrow" flip />
              <span className="shine" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ResultRow({ title, desc, value, unit }: { title: string; desc: string; value: string; unit: string }) {
  return (
    <div className="row">
      <div className="l">
        <b>{title}</b>
        <span>{desc}</span>
      </div>
      <div className="v">
        <span>{value}</span>
        <small>{unit}</small>
      </div>
    </div>
  );
}
